import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface MarkdownMessageProps {
  content: string
  className?: string
}

/**
 * Renders message content as Markdown with GFM support (tables, code blocks, lists, etc.)
 * Used in both Agent Training page and Agent Workspace ChatPanel.
 */
export default function MarkdownMessage({ content, className = '' }: MarkdownMessageProps) {
  return (
    <div className={`prose prose-sm max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Style overrides for chat context
          p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">
              {children}
            </a>
          ),
          code: ({ className: codeClass, children, ...props }) => {
            const isInline = !codeClass
            if (isInline) {
              return <code className="bg-gray-100 text-red-600 px-1 py-0.5 rounded text-xs" {...props}>{children}</code>
            }
            return (
              <pre className="bg-gray-900 text-green-300 p-3 rounded-md overflow-x-auto text-xs my-2">
                <code {...props}>{children}</code>
              </pre>
            )
          },
          ul: ({ children }) => <ul className="list-disc pl-4 mb-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-4 mb-1">{children}</ol>,
          li: ({ children }) => <li className="mb-0.5">{children}</li>,
          table: ({ children }) => (
            <div className="overflow-x-auto my-2">
              <table className="min-w-full border border-gray-300 text-xs">{children}</table>
            </div>
          ),
          th: ({ children }) => <th className="border border-gray-300 px-2 py-1 bg-gray-100 font-medium">{children}</th>,
          td: ({ children }) => <td className="border border-gray-300 px-2 py-1">{children}</td>,
          img: ({ src, alt }) => (
            <img src={src} alt={alt || ''} className="max-w-full max-h-60 rounded-md my-1" loading="lazy" />
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-gray-300 pl-3 italic text-gray-600 my-1">{children}</blockquote>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}
